<template>
  <div class="uploadManualEmptyLegsDashboard">
    <div class="uploadManualEmptyLegsDashboard__header">
      <div class="uploadManualEmptyLegsDashboard__title">Upload Manual Empty Legs</div>
    </div>

    <div class="uploadManualEmptyLegsDashboard__row">
      Excel file (.xlsx) with columns: Date, Aircraft, From Airport, To Airport, PRICE ALL IN, Operator
    </div>

    <input
      class="uploadManualEmptyLegsDashboard__row"
      type="file"
      accept=".xlsx,.xls"
      @change="parseFile"
      ref="fileInput"
    >

    <div v-if="parsedList.length" class="uploadManualEmptyLegsDashboard__row">
      Parsed rows: {{ parsedList.length }}
    </div>

    <div
      class="uploadManualEmptyLegsDashboard__button"
      :class="{ 'is-disabled': !parsedList.length || loading }"
      @click="upload"
    >
      <span class="text">{{ loading ? 'Uploading...' : 'Upload' }}</span>
    </div>

    <div v-if="resultMessage" class="uploadManualEmptyLegsDashboard__result">
      {{ resultMessage }}
    </div>

    <ul v-if="errors.length" class="uploadManualEmptyLegsDashboard__errors">
      <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
    </ul>
  </div>
</template>

<script>
import { aircraftsAPI } from '@/api/aircrafts'
import * as XLSX from 'xlsx'
import './UploadManualEmptyLegs.styl'

export default {
  name: 'upload-manual-empty-legs',
  data: () => ({
    parsedList: [],
    loading: false,
    resultMessage: '',
    errors: [],
  }),
  methods: {
    excelSerialToDateString (serial) {
      // Excel serial date → YYYY-MM-DD (epoch: 1899-12-30)
      const utcDays = Math.floor(serial - 25569)
      const date = new Date(utcDays * 86400 * 1000)
      const yyyy = date.getUTCFullYear()
      const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
      const dd = String(date.getUTCDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
    normalizeDate (value) {
      if (value == null || value === '') return null

      if (value instanceof Date && !isNaN(value.getTime())) {
        const yyyy = value.getUTCFullYear()
        const mm = String(value.getUTCMonth() + 1).padStart(2, '0')
        const dd = String(value.getUTCDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      }

      if (typeof value === 'number') {
        return this.excelSerialToDateString(value)
      }

      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed) return null
        // already ISO or MM/dd/yyyy — backend accepts both
        return trimmed
      }

      return null
    },
    async parseFile (event) {
      this.parsedList = []
      this.resultMessage = ''
      this.errors = []

      const file = event.target.files?.[0]
      if (!file) return

      try {
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = workbook.Sheets[sheetName]

        if (!sheet) {
          this.errors = ['Worksheet not found']
          return
        }

        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: true,
          defval: null,
        })

        const items = []

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i]
          if (!row || !row.length) continue

          const departureDate = this.normalizeDate(row[0])
          const modelName = row[1] != null ? String(row[1]).trim() : ''
          const departureCode = row[2] != null ? String(row[2]).trim() : ''
          const arrivalCode = row[3] != null ? String(row[3]).trim() : ''
          const exclusiveCost = Number(row[4])
          const operator = row[5] != null ? String(row[5]).trim() : ''

          if (!departureDate && !modelName && !departureCode && !arrivalCode && !operator) {
            continue
          }

          items.push({
            departureDate,
            modelName,
            departureCode,
            arrivalCode,
            exclusiveCost: isNaN(exclusiveCost) ? 0 : exclusiveCost,
            operator,
          })
        }

        this.parsedList = items

        if (!items.length) {
          this.errors = ['No data rows found in the file']
        }
      } catch (e) {
        console.error(e)
        this.errors = ['Failed to parse the file']
      }
    },
    async upload () {
      if (!this.parsedList.length || this.loading) return

      this.loading = true
      this.resultMessage = ''
      this.errors = []

      try {
        const { data } = await aircraftsAPI.uploadManualEmptyLegs(this.parsedList)
        const payload = data?.item ?? data?.Item ?? data

        const created = payload?.createdCount ?? payload?.CreatedCount ?? 0
        const skipped = payload?.skippedDuplicateCount ?? payload?.SkippedDuplicateCount ?? 0
        const apiErrors = payload?.errors ?? payload?.Errors ?? []

        this.resultMessage = `Created: ${created}, Skipped duplicates: ${skipped}`
        this.errors = Array.isArray(apiErrors) ? apiErrors : []

        if (!data?.isSuccessfull && !data?.IsSuccessfull && apiErrors.length === 0 && data?.message) {
          this.errors = [data.message]
        }
      } catch (e) {
        console.error(e)
        this.errors = [e?.response?.data?.message || e?.message || 'Upload failed']
      }

      this.loading = false
    },
  },
}
</script>
